import { ApiResponse, Member, MemberMenuPermission, Menu, Pageable, PageableParams } from "@/interface";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, TokenManager } from "@/lib/axios";
import { toast } from "@/lib/toast";
import { LoginRequest, LoginResponse, UserActiveListRequest, UserPasswordChangeRequest } from "@/interface";
import { getLocalISOString } from "@/lib/date";

// UI 상태 초기화 후 리다이렉트
function resetUiStateBeforeRedirect() {
  try {
    const keysToRemove = [
      "tab-history",
      "tabs",
      "menus",
      "recentRoutes",
      "lastVisitedRoute",
      "openPages",
      "layoutState",
    ];
    keysToRemove.forEach(k => localStorage.removeItem(k));
  } catch (e) {
    console.warn("resetUiStateBeforeRedirect error:", e);
  }
}

export const useUserList = (params: PageableParams) => {
  const { page = 0, size = 20, search = "", sort = "name,desc" } = params;
  return useQuery<Pageable<Member>>({
    queryKey: ["userList", page, size, search, sort],
    queryFn: () => api.get("/member", { params: { page, size, search, sort } }),
    select: (data: Pageable<Member>) => data,
  });
};

export const useUserActiveList = (params: UserActiveListRequest & { trigger?: number }) => {
  const { page = 0, size = 20, search = "", sort = "name,desc", isActive = null, name = "", trigger } = params;
  return useQuery<Pageable<Member>>({
    queryKey: ["userActiveList", page, size, search, sort, isActive, name, trigger],
    queryFn: () => api.get("/member/active", { params: { page, size, search, sort, isActive, name } }),
    select: (data: Pageable<Member>) => data,
  });
};

// 🔐 로그인 훅
export const useLogin = ({ redirectUrl = "/additional/memo" }: { redirectUrl?: string } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["login"],
    mutationFn: async (loginData: LoginRequest): Promise<LoginResponse> => {
      const response = await api.post<LoginResponse>("/login", loginData);
      if (!response.accessToken) throw new Error("토큰을 받지 못했습니다");
      return response;
    },
    onSuccess: async data => {
      // 토큰 저장
      TokenManager.setAccessToken(data.accessToken);
      TokenManager.setRefreshToken(data.refreshToken);
      // 사용자 정보 캐시
      queryClient.setQueryData(["user"], data);
      // 마지막 로그인 일자 업데이트
      await updateLoginDate({ name: data.name, date: getLocalISOString() }).catch(() => {});

      resetUiStateBeforeRedirect();
      // toast.success(`안녕하세요, ${data.id}님!`);

      window.location.replace(redirectUrl);
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || "로그인에 실패했습니다";
      toast.error(errorMessage);
    },
  });
};

// 🔐 로그아웃 훅
export const useLogout = () => {
  const queryClient = useQueryClient();
  // UI 상태 초기화
  const wipeUi = () => {
    try {
      const keys = ["tab-history", "tabs", "menus", "recentRoutes", "lastVisitedRoute", "openPages", "layoutState"];
      keys.forEach(k => localStorage.removeItem(k));
    } catch {}
  };

  return useMutation({
    mutationKey: ["logout"],
    mutationFn: async () => {
      // 백엔드에 로그아웃 요청 (선택적)
      try {
        await api.post("/logout");
      } catch (error: any) {
        toast.error(error.response?.data?.message || "로그아웃 중 오류가 발생했습니다");
      }
    },
    onSuccess: () => {
      // 토큰 제거
      TokenManager.removeAccessToken();
      TokenManager.removeRefreshToken();
      // 캐시 초기화
      queryClient.clear();
      // UI 상태 초기화
      wipeUi();
      // 로그인 페이지로 리다이렉트
      window.location.replace("/login");
    },
    onError: (error: any) => {
      // 실패해도 클라이언트에서 강제 로그아웃
      TokenManager.removeAccessToken();
      TokenManager.removeRefreshToken();
      queryClient.clear();
      wipeUi();
      toast.error(error.response?.data?.message || "로그아웃 중 오류가 발생했습니다");
      window.location.replace("/login");
    },
  });
};

export const useUserInfo = () => {
  return useQuery({
    queryKey: ["user"],
    queryFn: () => api.get("/user/info"),
    staleTime: Infinity, // 캐시된 데이터가 stale되지 않도록
    gcTime: Infinity, // 캐시가 만료되지 않도록 (v5에서는 gcTime으로 변경됨)
  });
};

export const useUserPasswordChange = () => {
  return useMutation({
    mutationFn: async (data: UserPasswordChangeRequest) => {
      const response = await api.post<ApiResponse>("/password/change", data);
      return response;
    },
    onSuccess: res => {},
    onError: err => {
      console.error(err);
      toast.error(err.message || "비밀번호 변경에 실패했습니다");
    },
  });
};

// 마지막 로그인 일자 업데이트
async function updateLoginDate({ name, date }: { name: string; date: string }) {
  try {
    await api.post("/updateLoginDate", { name, date });
  } catch (error) {
    console.error(error);
    // 로그인 일자 업데이트 실패는 로그인 성공을 방해하지 않도록 함
  }
}

export const useUpdateCompanyId = () => {
  return useMutation({
    mutationFn: async (data: { name: string; companyId: string }) => {
      const response = await api.put("/update/companyId", null, { params: { name: data.name, companyId: data.companyId } });
      return response;
    },
    onSuccess: res => {
      console.log(res);
      toast.success("회사 변경 성공");
    },
    onError: err => {
      console.error(err);
      toast.error("회사 변경 실패");
    },
  });
};
